
import { useEffect } from 'react';
import { useSiteSettings } from './useSiteSettings';

export const useFavicon = () => {
  const { data: siteSettings } = useSiteSettings();

  useEffect(() => {
    if (!siteSettings) return;

    // Update favicon
    if (siteSettings.faviconUrl) {
      updateFavicon(siteSettings.faviconUrl);
    }

    // Update Apple Touch Icon
    if (siteSettings.appleTouchIconUrl) {
      updateAppleTouchIcon(siteSettings.appleTouchIconUrl);
    }

    // Update PWA manifest
    if (siteSettings.pwaManifest && siteSettings.pwaIconUrl) {
      updatePWAManifest(siteSettings.pwaManifest, siteSettings.pwaIconUrl);
    }

  }, [siteSettings]);

  const updateFavicon = (faviconUrl: string) => {
    // Remove existing favicon links
    const existingFavicons = document.querySelectorAll('link[rel*="icon"]');
    existingFavicons.forEach(link => {
      if (!link.getAttribute('rel')?.includes('apple')) {
        link.remove();
      }
    });

    // Add new favicon
    const link = document.createElement('link');
    link.rel = 'icon';
    link.type = 'image/png';
    link.href = faviconUrl;
    document.head.appendChild(link);

    // Add additional sizes for better browser support
    const sizes = ['16x16', '32x32', '48x48'];
    sizes.forEach(size => {
      const sizedLink = document.createElement('link');
      sizedLink.rel = 'icon';
      sizedLink.type = 'image/png';
      sizedLink.setAttribute('sizes', size);
      sizedLink.href = faviconUrl;
      document.head.appendChild(sizedLink);
    });
  };

  const updateAppleTouchIcon = (appleTouchIconUrl: string) => {
    // Remove existing Apple touch icons
    const existingAppleIcons = document.querySelectorAll('link[rel*="apple-touch-icon"]');
    existingAppleIcons.forEach(link => link.remove());

    // Add new Apple touch icon
    const appleLink = document.createElement('link');
    appleLink.rel = 'apple-touch-icon';
    appleLink.href = appleTouchIconUrl;
    document.head.appendChild(appleLink);

    // Add different sizes for Apple devices
    const appleSizes = ['57x57', '60x60', '72x72', '76x76', '114x114', '120x120', '144x144', '152x152', '180x180'];
    appleSizes.forEach(size => {
      const sizedAppleLink = document.createElement('link');
      sizedAppleLink.rel = 'apple-touch-icon';
      sizedAppleLink.setAttribute('sizes', size);
      sizedAppleLink.href = appleTouchIconUrl;
      document.head.appendChild(sizedAppleLink);
    });
  };

  const updatePWAManifest = (manifest: any, pwaIconUrl: string) => {
    // Remove existing manifest
    const existingManifest = document.querySelector('link[rel="manifest"]');
    if (existingManifest) {
      existingManifest.remove();
    }

    // Create manifest with dynamic icons
    const fullManifest = {
      ...manifest,
      icons: [
        {
          src: pwaIconUrl,
          sizes: '192x192',
          type: 'image/png',
          purpose: 'any maskable'
        },
        {
          src: pwaIconUrl,
          sizes: '512x512',
          type: 'image/png',
          purpose: 'any maskable'
        }
      ]
    };

    // Create blob URL for manifest
    const manifestBlob = new Blob([JSON.stringify(fullManifest)], { type: 'application/json' });
    const manifestUrl = URL.createObjectURL(manifestBlob);

    // Add new manifest link
    const manifestLink = document.createElement('link');
    manifestLink.rel = 'manifest';
    manifestLink.href = manifestUrl;
    document.head.appendChild(manifestLink);

    // Add PWA meta tags
    updatePWAMetaTags(manifest);
  };

  const updatePWAMetaTags = (manifest: any) => {
    // Theme color
    let themeColorMeta = document.querySelector('meta[name="theme-color"]') as HTMLMetaElement;
    if (!themeColorMeta) {
      themeColorMeta = document.createElement('meta');
      themeColorMeta.name = 'theme-color';
      document.head.appendChild(themeColorMeta);
    }
    themeColorMeta.content = manifest.theme_color || '#000000';

    // Application name
    let appNameMeta = document.querySelector('meta[name="application-name"]') as HTMLMetaElement;
    if (!appNameMeta) {
      appNameMeta = document.createElement('meta');
      appNameMeta.name = 'application-name';
      document.head.appendChild(appNameMeta);
    }
    appNameMeta.content = manifest.name || 'VRAM Store';

    // Mobile web app capable
    let mobileCapableMeta = document.querySelector('meta[name="mobile-web-app-capable"]') as HTMLMetaElement;
    if (!mobileCapableMeta) {
      mobileCapableMeta = document.createElement('meta');
      mobileCapableMeta.name = 'mobile-web-app-capable';
      document.head.appendChild(mobileCapableMeta);
    }
    mobileCapableMeta.content = 'yes';

    // Apple mobile web app capable
    let appleMobileCapableMeta = document.querySelector('meta[name="apple-mobile-web-app-capable"]') as HTMLMetaElement;
    if (!appleMobileCapableMeta) {
      appleMobileCapableMeta = document.createElement('meta');
      appleMobileCapableMeta.name = 'apple-mobile-web-app-capable';
      document.head.appendChild(appleMobileCapableMeta);
    }
    appleMobileCapableMeta.content = 'yes';

    // Apple mobile web app status bar style
    let appleStatusBarMeta = document.querySelector('meta[name="apple-mobile-web-app-status-bar-style"]') as HTMLMetaElement;
    if (!appleStatusBarMeta) {
      appleStatusBarMeta = document.createElement('meta');
      appleStatusBarMeta.name = 'apple-mobile-web-app-status-bar-style';
      document.head.appendChild(appleStatusBarMeta);
    }
    appleStatusBarMeta.content = 'default';
  };

  return null;
};
